import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Typography } from "@mui/material";
import React from "react";

export interface ReviewRecipient {
  email: string;
  name: string;
  message: string;
  /** Set when she was contacted inside the skip window — shown per row. */
  warning?: string;
}

interface Props {
  recipients: ReviewRecipient[];
  sending: boolean;
  onSend: () => void;
  onClose: () => void;
}

/**
 * Read every message before a bulk send goes out.
 *
 * The send button used to fire immediately: one click sent a personalised
 * message to everyone ticked, and the only way to know what any of them said was
 * to read the template and imagine it filled in. For a screen whose whole job is
 * writing to members who are already leaving, that's the wrong amount of
 * friction — a mistake here reaches dozens of people at their most delicate
 * moment.
 *
 * So the button now opens this: each recipient with her exact text, in order,
 * scrollable, with the send at the bottom. Nothing is sent until you've had the
 * chance to scroll past it.
 */
const BulkReviewPanel: React.FC<Props> = ({ recipients, sending, onSend, onClose }) => {
  const flagged = recipients.filter((r) => r.warning).length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            📋 Review {recipients.length} message{recipients.length === 1 ? "" : "s"} before sending
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            ✕
          </IconButton>
        </Box>

        <Alert severity="info">
          This is exactly what each member will receive. Scroll through them, then send. To change
          the wording, close this and use “Edit wording”; to change who gets it, untick her in the
          table.
        </Alert>

        {flagged > 0 && (
          <Alert severity="warning">
            {flagged} of these {flagged === 1 ? "was" : "were"} messaged recently by another tool.
          </Alert>
        )}

        <Box sx={{ maxHeight: "min(420px, 50vh)", overflowY: "auto", pr: 1 }}>
          <Stack spacing={1.5}>
            {recipients.map((recipient, index) => (
              <Paper key={recipient.email} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {index + 1}/{recipients.length}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {recipient.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {recipient.email}
                  </Typography>
                  {recipient.warning && (
                    <Chip size="small" color="warning" label={recipient.warning} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {recipient.message}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Box display="flex" gap={1}>
          <Button variant="contained" onClick={onSend} disabled={sending}>
            {sending ? "Sending…" : `✔ Send all ${recipients.length}`}
          </Button>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default BulkReviewPanel;
